<template lang="pug">
component(
    v-if="renderWidget"
    v-bind="wrapperOptions"
    :data="data"
)
    //- Widgets that render children widgets will provide a slot that passes the widget data back out so it can render another widget in its slot
    //- it should always provide its child widget data.
    //- spaceLeft doesn't have to be calculated on every single child widget, so if we don't have any space left being provided to the slot we fallback to what the parent's spaceLeft is.
    //- widgetProps allows a parent widget to pass props down to any child widget that it may render. It is the parents responsibility to make sure it only passes down props that its children expect.
    template(#default="{ data: widget, spaceLeft: widgetSpaceLeft, ...widgetProps }")
        Widget(
            v-if="widget"
            :data="widget"
            :spaceLeft="widgetSpaceLeft || spaceLeft"
            :widgetProps="widgetProps"
        )

</template>

<script>
import {
    bindStoryblokBridgeData,
    generatePaddingClasses,
    generateBackgroundColorClass,
} from '../helpers'
import ButtonWidget from './widgets/button'
import CartIconWidget from './widgets/cartIcon'
import ColumnWidget from './widgets/column'
import FitmentWidget from './widgets/fitment'
import GeoTextWidget from './widgets/geoText'
import ImageBulletWidget from './widgets/imageBullet'
import ImageCarouselWidget from './widgets/imageCarousel'
import ImageWidget from './widgets/imageWidget'
import SiteSearchWidget from './widgets/siteSearch'
import SvgWidget from './widgets/svgWidget'
import SectionWidget from './widgets/section'
import TextWidget from './widgets/text'
import VideoBannerWidget from './widgets/videoBanner'
import ImageCardWidget from './widgets/imageCard'
import ConstrainWidget from './widgets/constrain'
import SearchWrapperWidget from './widgets/search/wrapper'
import TagsWidget from './widgets/tags'

// lazyload all non-important widgets
const AccordionWidget = () =>
    import(/* webpackChunkName: "SB-AccordionWidget" */ './widgets/accordion')

const ReadMoreWidget = () =>
    import(/* webpackChunkName: "SB-ReadMoreWidget" */ './widgets/readMore')

const AuthorWidget = () => import(/* webpackChunkName: "SB-AuthorWidget" */ './widgets/author')
const InstallerNetWidget = () =>
    import(/* webpackChunkName: "SB-InstallerWidget" */ './widgets/installernet')
const GlobalContentSection = () =>
    import(/* webpackChunkName: "SB-GlobalContentSection" */ './widgets/globalContentSection')

const JotFormWidget = () => import(/* webpackChunkName: "SB-JotFormWidget" */ './widgets/jotForm')
const LevelWidget = () => import(/* webpackChunkName: "SB-LevelWidget" */ './widgets/level')
const LinkCardWidget = () =>
    import(/* webpackChunkName: "SB-LinkCardWidget" */ './widgets/linkCard')
const PayPalMessageWidget = () =>
    import(/* webpackChunkName: "SB-PayPalMessageWidget" */ './widgets/paypal/message.vue')
const RecommendationsWidget = () =>
    import(/* webpackChunkName: "SB-RecommendationsWidget" */ './widgets/recommendations')

const SearchInputWidget = () =>
    import(/* webpackChunkName: "SB-SearchWrapper-input" */ './widgets/search/input')

const SearchDisplayWidget = () =>
    import(/* webpackChunkName: "SB-SearchWrapper-display" */ './widgets/search/display')

const SpacerWidget = () => import(/* webpackChunkName: "SB-SpacerWidget" */ './widgets/spacer')
const SubscribeWidget = () =>
    import(/* webpackChunkName: "SB-SubscribeWidget" */ './widgets/subscribe/inline')
const SubscribeFormWidget = () =>
    import(/* webpackChunkName: "SB-SubscribeFormWidget" */ './widgets/subscribe/form')
const TableWidget = () => import(/* webpackChunkName: "SB-TableWidget" */ './widgets/table/')
const VideoButtonWidget = () =>
    import(/* webpackChunkName: "SB-VideoButtonWidget" */ './widgets/videoButton')
const VideoWidget = () => import(/* webpackChunkName: "SB-VideoWidget" */ './widgets/video')
const VisibilityWidget = () =>
    import(/* webpackChunkName: "SB-VisibilityWidget" */ './widgets/visibility')
const YotpoReviewCarouselWidget = () =>
    import(/* webpackChunkName: "SB-YotpoReviewCarouselWidget" */ './widgets/yotpo/reviewsCarousel')
const YotpoSiteReviewsWidget = () =>
    import(/* webpackChunkName: "SB-YotpoSiteReviewsWidget" */ './widgets/yotpo/siteReviews')
const YotpoSocialImagesWidget = () =>
    import(/* webpackChunkName: "SB-YotpoSocialImagesWidget" */ './widgets/yotpo/socialImages')

export default {
    name: 'Widget',
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        spaceLeft: {
            type: Object,
            default() {
                return {}
            },
        },
        widgetProps: {
            type: Object,
            default() {
                return {}
            },
        },
    },
    computed: {
        wrapperOptions() {
            const options = {
                is: null,
                // Widget Classes
                class: [],
                // ID for the widget, used for scrolling
                ...(this.data.id && { id: this.data.id }),
                // Adds the storyblok editor click events
                ...bindStoryblokBridgeData(this.data),
                // Props that can be passed from the parent widget into its children.
                ...this.widgetProps,
            }

            let addWidgetDefaultClasses = true
            switch (this.data.component) {
                case 'accordion-widget':
                    options.is = AccordionWidget
                    addWidgetDefaultClasses = false
                    break

                case 'read-more-widget':
                    options.is = ReadMoreWidget
                    break

                case 'author-widget':
                    options.is = AuthorWidget
                    break
                case 'button-widget':
                    options.is = ButtonWidget
                    break

                case 'cart-icon-widget':
                    options.is = CartIconWidget
                    break

                case 'column-widget':
                    options.is = ColumnWidget
                    options.spaceLeft = this.spaceLeft
                    break

                case 'constrain-widget':
                    options.is = ConstrainWidget
                    options.spaceLeft = this.spaceLeft
                    break

                case 'fitment-widget':
                    options.is = FitmentWidget
                    break

                case 'geo-text-widget':
                    options.is = GeoTextWidget
                    break

                case 'global-content-section':
                    options.is = GlobalContentSection
                    break

                case 'image-bullet-widget':
                    options.is = ImageBulletWidget
                    break

                case 'image-card-widget':
                    options.is = ImageCardWidget
                    options.spaceLeft = this.spaceLeft
                    break

                case 'image-carousel-widget':
                    options.is = ImageCarouselWidget
                    break

                case 'image-widget':
                    options.is = ImageWidget
                    options.spaceLeft = this.spaceLeft
                    break

                case 'installernet-widget':
                    options.is = InstallerNetWidget
                    break

                case 'jotform-widget':
                    options.is = JotFormWidget
                    break

                case 'level-widget':
                    options.is = LevelWidget
                    break

                case 'link-card-widget':
                    options.is = LinkCardWidget
                    break

                case 'paypal-message-widget':
                    options.is = PayPalMessageWidget
                    break

                case 'recommendations-widget':
                    options.is = RecommendationsWidget
                    break

                case 'section':
                    options.is = SectionWidget
                    addWidgetDefaultClasses = false
                    break
                case 'search-section-content':
                case 'search-section-product':
                    options.is = SearchWrapperWidget
                    break
                case 'search-input':
                    options.is = SearchInputWidget
                    break
                case 'search-display':
                    options.is = SearchDisplayWidget
                    break

                case 'site-search-widget':
                    options.is = SiteSearchWidget
                    break

                case 'spacer-widget':
                    options.is = SpacerWidget
                    break

                case 'subscribe-widget':
                    options.is = SubscribeWidget
                    break

                case 'subscribe-form-widget':
                    options.is = SubscribeFormWidget
                    break

                case 'svg-widget':
                    options.is = SvgWidget
                    options.spaceLeft = this.spaceLeft
                    break

                case 'tags-widget':
                    options.is = TagsWidget
                    break

                case 'text-widget':
                    options.is = TextWidget
                    break

                case 'table-widget':
                    options.is = TableWidget
                    break

                case 'video-banner-widget':
                    options.is = VideoBannerWidget
                    break

                case 'video-button-widget':
                    options.is = VideoButtonWidget
                    break

                case 'video-widget':
                    options.is = VideoWidget
                    break

                case 'visibility-widget':
                    options.is = VisibilityWidget
                    break

                case 'yotpo-review-carousel-widget':
                    options.is = YotpoReviewCarouselWidget
                    break

                case 'yotpo-site-reviews-widget':
                    options.is = YotpoSiteReviewsWidget
                    break

                case 'yotpo-social-images-widget':
                    options.is = YotpoSocialImagesWidget
                    break

                default:
                    options.is = null
                    break
            }

            if (addWidgetDefaultClasses) {
                options.class.push(
                    generatePaddingClasses(
                        this.data.mobilePadding,
                        this.data.tabletPadding,
                        this.data.desktopPadding
                    ),
                    generateBackgroundColorClass(this.data.backgroundColor)
                )
            }

            return options
        },
        renderWidget() {
            return this.wrapperOptions.is && this.$storyblok.isVisible(this.data)
        },
    },
}
</script>
